"use client";

import { useState } from "react";
import { type AutomaticStreakConfig, type CampaignAutopilotConfigV1Data } from "@cede/types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PortalModal } from "@/components/ui/portal-modal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useAutomaticStreakConfigForm, type AutomaticStreakConfigFormData } from "@/hooks/automatic-streak";
import { cn } from "@/lib/utils";
import { formatStreakDuration } from "@/utils/automatic-streak";
import { formatBalance } from "@/utils/hyperliquid/numeric";
import {
  AlertCircle,
  HelpCircle,
  Info,
  CheckCircle2,
  Loader2,
  ArrowDown,
  ArrowRight,
  Clock,
  Repeat,
} from "lucide-react";

// Re-export V2 components for convenience
export {
  AutomaticStreakConfigFormV2,
  AUTOMATIC_STREAK_CONFIG_FORM_V2_ID,
  type AutomaticStreakConfigFormV2Props,
  type AutomaticStreakConfigFormV2Data,
} from "./v2";

export type { AutomaticStreakConfigFormData };

/** Stable form ID for external button association */
export const AUTOMATIC_STREAK_CONFIG_FORM_ID = "automatic-streak-config-form";

export interface AutomaticStreakConfigFormProps {
  onSubmit: (data: AutomaticStreakConfigFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  pairSymbol: string;
  accountId: string;
  exchangeId: string;
  className?: string;
  /** Config can be either the full AutomaticStreakConfig or just the V1 data fields */
  config: AutomaticStreakConfig | CampaignAutopilotConfigV1Data;
  hideActions?: boolean;
  onValidityChange?: (isValid: boolean) => void;
  formId?: string;
}

interface CompactFieldProps {
  label: string;
  children: React.ReactNode;
  error?: string;
  tooltip?: string;
}

const CompactField = ({ label, children, error, tooltip }: CompactFieldProps) => (
  <div className="space-y-1">
    <div className="flex items-center gap-1">
      <Label className="text-cede-text-secondary text-xs">{label}</Label>
      {tooltip && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Info className="w-3 h-3 text-cede-text-tertiary cursor-help" />
          </TooltipTrigger>
          <TooltipContent className="max-w-[200px]">{tooltip}</TooltipContent>
        </Tooltip>
      )}
    </div>
    {children}
    {error && (
      <p className="text-xs text-cede-error flex items-center gap-1">
        <AlertCircle className="w-3 h-3" />
        {error}
      </p>
    )}
  </div>
);

/**
 * V1 Form Component - Original cycle-based configuration
 */
export const AutomaticStreakConfigForm = ({
  onSubmit,
  onCancel,
  isSubmitting = false,
  pairSymbol,
  accountId,
  exchangeId,
  className,
  config,
  hideActions = false,
  onValidityChange,
  formId = AUTOMATIC_STREAK_CONFIG_FORM_ID,
}: AutomaticStreakConfigFormProps) => {
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  // Use the form hook for all state, calculations, and validation
  const {
    fields,
    setters,
    balance,
    calculations,
    validation,
    intervalOptions,
    configBounds,
    handleFieldBlur,
    handleSubmit,
  } = useAutomaticStreakConfigForm({
    accountId,
    exchangeId,
    pairSymbol,
    config,
    onSubmit,
    onValidityChange,
  });

  const {
    totalTrades,
    totalTradesParsed,
    volumePerCycle,
    volumeMin,
    volumeMax,
    variancePercent,
    intervalSeconds,
    startDelayMinutes,
    alsoRunForNextEpoch,
  } = fields;
  const { setTotalTrades, setVolumePerCycle, setIntervalSeconds, setStartDelayMinutes, setAlsoRunForNextEpoch } =
    setters;
  const {
    value: balanceValue,
    quoteToken,
    isLoading: isBalanceLoading,
    requiredBalance,
    isSufficient: isBalanceSufficient,
  } = balance;
  const { totalDurationSeconds, totalVolumeEstimate, scheduledStartTime } = calculations;
  const { errors, isFormValid } = validation;

  return (
    <>
      <form id={formId} onSubmit={handleSubmit} className={cn("p-4 space-y-3", className)}>
        {/* Header with How it works */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-cede-text-secondary">{pairSymbol}</p>
          <button
            type="button"
            onClick={() => setShowHowItWorks(true)}
            className="flex items-center gap-1 text-xs text-cede-primary hover:underline"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            How it works?
          </button>
        </div>

        {/* Balance Display - inline style without background */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <span className="text-xs text-cede-text-secondary">Available balance</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="w-3 h-3 text-cede-text-tertiary cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-[220px]">
                5% + 0.5% per cycle reserved for fees and price fluctuations.
              </TooltipContent>
            </Tooltip>
          </div>
          {isBalanceLoading ? (
            <Loader2 className="w-4 h-4 animate-spin text-cede-text-tertiary" />
          ) : (
            <span className="text-sm font-medium text-cede-text-primary">
              {formatBalance(balanceValue)} {quoteToken}
            </span>
          )}
        </div>

        {/* Form Fields - Compact Layout */}
        <div className="space-y-2">
          {/* Number of Trades */}
          <CompactField
            label={`Number of cycles (${config.minTrades}-${config.maxTrades})`}
            error={errors.totalTrades}
            tooltip="Each cycle executes a BUY order, waits a few seconds, then executes a SELL order"
          >
            <Input
              type="number"
              min={config.minTrades}
              max={config.maxTrades}
              value={totalTrades}
              onChange={(e) => setTotalTrades(e.target.value)}
              onBlur={handleFieldBlur}
              className="bg-cede-bg-component [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </CompactField>

          {/*Single Volume Input */}
          <CompactField
            label={`Volume per cycle (${quoteToken})`}
            error={errors.volumePerCycle}
            tooltip={`Each cycle trades within ±${variancePercent}% of this amount. Range: ${formatBalance(
              volumeMin,
            )} - ${formatBalance(volumeMax)} ${quoteToken}`}
          >
            <Input
              type="number"
              step="0.01"
              min={configBounds.minVolumePerCycle}
              max={config.maxVolumePerTrade}
              value={volumePerCycle}
              onChange={(e) => setVolumePerCycle(e.target.value)}
              onBlur={handleFieldBlur}
              placeholder={`${configBounds.minVolumePerCycle.toFixed(2)}-${config.maxVolumePerTrade}`}
              className="bg-cede-bg-component [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
          </CompactField>

          {/* Interval + Start Delay - Same Row */}
          <div className="grid grid-cols-2 gap-2">
            <CompactField
              label="Interval"
              error={errors.intervalSeconds}
              tooltip="Time between the start of each cycle"
            >
              <Select value={intervalSeconds.toString()} onValueChange={(value) => setIntervalSeconds(parseInt(value))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {intervalOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value.toString()}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CompactField>

            <CompactField label="Start in (min)" error={errors.startDelay}>
              <Input
                type="number"
                min={0}
                max={60}
                value={startDelayMinutes}
                onChange={(e) => setStartDelayMinutes(e.target.value)}
                onBlur={handleFieldBlur}
                className="bg-cede-bg-component [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
            </CompactField>
          </div>
        </div>

        {/* Also run for next epoch checkbox */}
        <div className="flex items-center space-x-2">
          <Checkbox
            id="also-run-next-epoch"
            checked={alsoRunForNextEpoch}
            onCheckedChange={(checked) => setAlsoRunForNextEpoch(checked === true)}
          />
          <label
            htmlFor="also-run-next-epoch"
            className="text-xs text-cede-text-secondary cursor-pointer flex items-center gap-1"
          >
            <Repeat className="w-3 h-3" />
            Also run for next epoch
          </label>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="w-3 h-3 text-cede-text-tertiary cursor-help" />
            </TooltipTrigger>
            <TooltipContent className="max-w-[200px]">
              Create another autopilot run with the same settings for the next campaign epoch
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Summary Section - Ultra Compact */}
        <div className="text-xs border-t border-cede-border-container pt-2 space-y-0.5">
          <div className="flex justify-between items-center">
            <span className="text-cede-text-secondary">Duration</span>
            <span className="text-cede-text-primary">
              {formatStreakDuration(totalDurationSeconds)} · {totalTradesParsed} cycles
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-cede-text-secondary">Est. volume</span>
            <div className="flex items-center gap-2">
              <span className="text-cede-text-primary">
                ~{formatBalance(totalVolumeEstimate)} {quoteToken}
              </span>
              <span
                className={cn(
                  "flex items-center gap-0.5",
                  isBalanceSufficient ? "text-cede-success" : "text-cede-error",
                )}
              >
                {isBalanceSufficient ? <CheckCircle2 className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
              </span>
            </div>
          </div>
          {!isBalanceSufficient && !isBalanceLoading && (
            <p className="text-cede-error pt-0.5">
              Need ~{formatBalance(requiredBalance)} {quoteToken}. Reduce cycles or max volume.
            </p>
          )}
          <p className="text-cede-text-tertiary">Starts at {scheduledStartTime.toLocaleTimeString()}</p>
        </div>

        {/* Actions */}
        {!hideActions && (
          <div className="flex gap-3 pt-1">
            <Button
              type="button"
              variant="secondary"
              size="medium"
              onClick={onCancel}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              size="medium"
              disabled={isSubmitting || !isFormValid}
              className="flex-1"
            >
              {isSubmitting ? "Creating..." : "Start Autopilot"}
            </Button>
          </div>
        )}
      </form>

      {/* How it works modal */}
      <PortalModal open={showHowItWorks} onOpenChange={setShowHowItWorks} className="sm:max-w-[480px]">
        <PortalModal.Header>
          <PortalModal.Title>How Autopilot Works</PortalModal.Title>
          <PortalModal.Description>Automatically executes buy/sell cycles on {pairSymbol}</PortalModal.Description>
        </PortalModal.Header>
        <PortalModal.Content className="space-y-5">
          {/* Visual Timeline */}
          <div className="bg-cede-bg-component rounded-lg p-4 space-y-3">
            <p className="text-xs font-medium text-cede-text-secondary uppercase tracking-wide">Cycle Timeline</p>

            {/* Cycle 1 */}
            <div className="flex items-start gap-3">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-cede-success/20 flex items-center justify-center">
                  <ArrowDown className="w-4 h-4 text-cede-success" />
                </div>
                <div className="w-0.5 h-4 bg-cede-border-container" />
              </div>
              <div className="pt-1">
                <p className="text-sm font-medium text-cede-success">BUY</p>
                <p className="text-xs text-cede-text-tertiary">Random volume (±{variancePercent}% variance)</p>
              </div>
            </div>

            {/* Delay indicator */}
            <div className="flex items-start gap-3">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-cede-bg-component border border-dashed border-cede-border-container flex items-center justify-center">
                  <Clock className="w-4 h-4 text-cede-text-tertiary" />
                </div>
                <div className="w-0.5 h-4 bg-cede-border-container" />
              </div>
              <div className="pt-1">
                <p className="text-sm text-cede-text-secondary">Wait 1-20 seconds</p>
                <p className="text-xs text-cede-text-tertiary">Randomized for natural patterns</p>
              </div>
            </div>

            {/* Sell */}
            <div className="flex items-start gap-3">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-cede-error/20 flex items-center justify-center">
                  <ArrowRight className="w-4 h-4 text-cede-error rotate-90" />
                </div>
                <div className="w-0.5 h-4 bg-cede-border-container" />
              </div>
              <div className="pt-1">
                <p className="text-sm font-medium text-cede-error">SELL</p>
                <p className="text-xs text-cede-text-tertiary">Same amount as bought</p>
              </div>
            </div>

            {/* Interval */}
            <div className="flex items-start gap-3">
              <div className="flex flex-col items-center">
                <div className="w-8 h-8 rounded-full bg-cede-primary/20 flex items-center justify-center">
                  <Repeat className="w-4 h-4 text-cede-primary" />
                </div>
              </div>
              <div className="pt-1">
                <p className="text-sm text-cede-text-secondary">Wait your interval</p>
                <p className="text-xs text-cede-text-tertiary">Then repeat next cycle</p>
              </div>
            </div>
          </div>

          {/* Key Points */}
          <div className="space-y-2">
            <p className="text-xs font-medium text-cede-text-secondary uppercase tracking-wide">Key Points</p>
            <div className="grid gap-2">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-cede-success mt-0.5 shrink-0" />
                <p className="text-sm text-cede-text-secondary">
                  <strong className="text-cede-text-primary">1 cycle = 1 buy + 1 sell</strong> executed within seconds
                </p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-cede-success mt-0.5 shrink-0" />
                <p className="text-sm text-cede-text-secondary">
                  Volume is <strong className="text-cede-text-primary">randomized</strong> within ±{variancePercent}% of
                  your configured amount
                </p>
              </div>
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-cede-success mt-0.5 shrink-0" />
                <p className="text-sm text-cede-text-secondary">
                  Ensure enough <strong className="text-cede-text-primary">{quoteToken}</strong> balance for all cycles
                </p>
              </div>
              <div className="flex items-start gap-2">
                <Info className="w-4 h-4 text-cede-warning mt-0.5 shrink-0" />
                <p className="text-sm text-cede-text-secondary">
                  Small fees and slippage may slightly reduce your balance
                </p>
              </div>
            </div>
          </div>
        </PortalModal.Content>
        <PortalModal.Footer>
          <Button variant="primary" size="medium" onClick={() => setShowHowItWorks(false)} className="w-full">
            Got it
          </Button>
        </PortalModal.Footer>
      </PortalModal>
    </>
  );
};
